/**
 * Browser libopus codec — a tiny glue around a vendored `opus.wasm`.
 *
 * This is the ONE codec used on every platform (Android WebView, iOS WKWebView,
 * desktop browsers): plain WebAssembly + typed arrays, nothing platform-specific.
 * `opus.wasm` is the official libopus compiled to a clean C-ABI module (vendored
 * from the BSD-licensed `@evan/opus`); the wasm bytes are fetched once through
 * the project's bundled-first/server-fallback asset loader, so it works offline
 * exactly like the voice/SFX assets.
 *
 * PCM is interleaved **Int16** (what opus_encode/opus_decode take/return). One
 * `encode()`/`decode()` call is fully synchronous, so the single shared scratch
 * buffers are safe even with a live encoder and decoder at once (JS is
 * single-threaded — calls never interleave mid-flight).
 */
import { loadAsset } from '../asset';

// opus_encoder_ctl / opus_decoder_ctl request codes (from the libopus API).
export const OPUS_APPLICATION_VOIP = 2048;
export const OPUS_SIGNAL_VOICE = 3001;
const CTL_SET_BITRATE = 4002;
const CTL_SET_COMPLEXITY = 4010;
const CTL_SET_VBR = 4006;
const CTL_SET_SIGNAL = 4024;
const CTL_SET_DTX = 4016;

const PACKET_SCRATCH = 1 << 13; // 8 KiB — max encoded packet out / packet in
const PCM_SCRATCH = 1 << 15; // 32 KiB — max PCM bytes (16384 Int16 samples)
const DECODE_MAX_SAMPLES = 5760; // 120 ms @ 48 kHz — libopus max frame

interface OpusExports {
  memory: WebAssembly.Memory;
  malloc(n: number): number;
  free(p: number): void;
  opus_strerror(code: number): number;
  opus_encoder_get_size(channels: number): number;
  opus_encoder_init(ptr: number, fs: number, channels: number, application: number): number;
  opus_encoder_ctl_set(ptr: number, request: number, value: number): number;
  opus_encoder_ctl_get(ptr: number, request: number): number;
  opus_encode(ptr: number, pcm: number, frameSize: number, out: number, maxBytes: number): number;
  opus_decoder_get_size(channels: number): number;
  opus_decoder_init(ptr: number, fs: number, channels: number): number;
  opus_decode(ptr: number, data: number, len: number, pcm: number, frameSize: number, fec: number): number;
}

interface OpusModule {
  wasm: OpusExports;
  packetPtr: number;
  pcmPtr: number;
  u8: Uint8Array;
}

let modulePromise: Promise<OpusModule> | null = null;

/** Instantiate the wasm once; memoised for the session. */
function loadModule(): Promise<OpusModule> {
  if (modulePromise) return modulePromise;
  modulePromise = (async () => {
    const bytes = await loadAsset('opus.wasm');
    if (!bytes) throw new Error('opus.wasm not found');
    const m: { wasm?: OpusExports; u8?: Uint8Array; packetPtr?: number; pcmPtr?: number } = {};
    const instance = await WebAssembly.instantiate(bytes, {
      wasi_snapshot_preview1: { fd_seek: () => 0, fd_write: () => 0, fd_close: () => 0, proc_exit: () => {} },
      env: {
        emscripten_notify_memory_growth() {
          if (m.wasm) m.u8 = new Uint8Array(m.wasm.memory.buffer);
        },
      },
    });
    const wasm = instance.instance.exports as unknown as OpusExports;
    m.wasm = wasm;
    m.packetPtr = wasm.malloc(PACKET_SCRATCH);
    m.pcmPtr = wasm.malloc(PCM_SCRATCH);
    m.u8 = new Uint8Array(wasm.memory.buffer);
    return m as OpusModule;
  })();
  return modulePromise;
}

function strError(m: OpusModule, ptr: number): string {
  let s = '';
  const u8 = m.u8;
  for (let o = ptr; o < u8.length && u8[o] !== 0; o++) s += String.fromCharCode(u8[o]);
  return s;
}
/** Throw on a negative libopus return code; otherwise pass the value through. */
function check(m: OpusModule, code: number): number {
  if (code < 0) throw new Error('opus: ' + strError(m, m.wasm.opus_strerror(code)));
  return code;
}

export class OpusEncoder {
  private ptr: number;
  constructor(
    private readonly m: OpusModule,
    private readonly channels: number,
    sampleRate: number,
    application: number,
  ) {
    const w = m.wasm;
    this.ptr = w.malloc(w.opus_encoder_get_size(channels));
    try {
      check(m, w.opus_encoder_init(this.ptr, sampleRate, channels, application));
    } catch (e) {
      w.free(this.ptr);
      this.ptr = 0;
      throw e;
    }
  }
  ctl(request: number, value: number): void {
    check(this.m, this.m.wasm.opus_encoder_ctl_set(this.ptr, request, value));
  }
  /** Encode one frame of interleaved Int16 PCM → a fresh Opus packet (copy). */
  encode(pcm: Int16Array): Uint8Array {
    const m = this.m;
    const src = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
    m.u8.set(src, m.pcmPtr);
    const n = check(
      m,
      m.wasm.opus_encode(this.ptr, m.pcmPtr, pcm.length / this.channels, m.packetPtr, PACKET_SCRATCH),
    );
    return m.u8.slice(m.packetPtr, m.packetPtr + n);
  }
  close(): void {
    if (this.ptr) {
      this.m.wasm.free(this.ptr);
      this.ptr = 0;
    }
  }
}

export class OpusDecoder {
  private ptr: number;
  constructor(
    private readonly m: OpusModule,
    private readonly channels: number,
    sampleRate: number,
  ) {
    const w = m.wasm;
    this.ptr = w.malloc(w.opus_decoder_get_size(channels));
    try {
      check(m, w.opus_decoder_init(this.ptr, sampleRate, channels));
    } catch (e) {
      w.free(this.ptr);
      this.ptr = 0;
      throw e;
    }
  }
  /** Decode one Opus packet → interleaved Int16 PCM (a fresh copy). */
  decode(packet: Uint8Array): Int16Array {
    const m = this.m;
    m.u8.set(packet, m.packetPtr);
    const samples = check(
      m,
      m.wasm.opus_decode(this.ptr, m.packetPtr, packet.length, m.pcmPtr, DECODE_MAX_SAMPLES, 0),
    );
    const bytes = m.u8.slice(m.pcmPtr, m.pcmPtr + 2 * this.channels * samples);
    return new Int16Array(bytes.buffer, 0, samples * this.channels);
  }
  close(): void {
    if (this.ptr) {
      this.m.wasm.free(this.ptr);
      this.ptr = 0;
    }
  }
}

/** Create a mono VOIP encoder with DTX (silence ≈ 0 bytes) at the given bitrate. */
export async function createVoiceEncoder(sampleRate: number, bitrate: number): Promise<OpusEncoder> {
  const m = await loadModule();
  const enc = new OpusEncoder(m, 1, sampleRate, OPUS_APPLICATION_VOIP);
  enc.ctl(CTL_SET_BITRATE, bitrate);
  enc.ctl(CTL_SET_VBR, 1);
  enc.ctl(CTL_SET_DTX, 1);
  enc.ctl(CTL_SET_SIGNAL, OPUS_SIGNAL_VOICE);
  enc.ctl(CTL_SET_COMPLEXITY, 5);
  return enc;
}

/** Create a mono decoder. */
export async function createVoiceDecoder(sampleRate: number): Promise<OpusDecoder> {
  const m = await loadModule();
  return new OpusDecoder(m, 1, sampleRate);
}

/** Best-effort warm-up so the first broadcast/playback isn't delayed by the fetch. */
export function preloadOpus(): void {
  void loadModule().catch(() => {});
}
